import { useCallback, useEffect, useLayoutEffect, useRef, type MutableRefObject, type RefObject } from 'react';

import { trackEventMixpanel } from 'utils/mixpanel';
import { PPA_PACK_ARCH_TABLE_SCROLLED } from 'utils/mixpanelEventNames';

import {
    CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS,
    HORIZONTAL_PAGINATION_RIGHT_EDGE_THRESHOLD_PX,
    setPackArchitectureTableScrollLeft,
    TABLE_SEGMENT_COLUMN_WIDTH_PX,
    TABLE_PAGINATION_SCROLL_RETREAT_PX,
    TABLE_VENDOR_COLUMN_WIDTH_PX,
} from './packArchitectureTable.helpers';

type ScrollSnapshot = {
    scrollLeft: number;
    scrollWidth: number;
    columnCountBeforeFetch: number;
};

type UsePackArchitectureTableScrollParams = {
    tableWrapperRef: RefObject<HTMLDivElement | null>;
    builtTableRowCount: number;
    visibleColumnCount: number;
    loadedColumnCount: number;
    tableHasMorePages: boolean;
    isChartLoading: boolean;
    isTablePaginatedLoading: boolean;
    isChartPaginatedLoading: boolean;
    fetchNextTablePage: () => Promise<void>;
    activeSegmentKey: string | null;
    selectedColumnKey: string | null;
    columnIndexByKey?: Map<string, number>;
    rowIndexByKey?: Map<string, number>;
    rowHeightPx?: number;
    isSyncingTableFromChartRef?: MutableRefObject<boolean>;
    suppressChartToTableSyncRef?: MutableRefObject<boolean>;
};

const PAGINATION_SCROLL_RETRY_MS = [0, 50, 150];
const TABLE_PAGINATION_SUPPRESS_CHART_SYNC_CLEAR_MS =
    Math.max(...PAGINATION_SCROLL_RETRY_MS, ...CHART_TO_TABLE_POST_PAGINATION_SYNC_RETRY_MS) + 50;
const TABLE_SCROLL_TRACK_DEBOUNCE_MS = 500;
const FALLBACK_ROW_HEIGHT_PX = 40;

const getAverageRowHeightPx = (tableBody: HTMLElement) => {
    const rows = tableBody.querySelectorAll('tbody tr.ant-table-row, [data-row-key]');
    if (rows.length === 0) return FALLBACK_ROW_HEIGHT_PX;

    const sampleCount = Math.min(rows.length, 8);
    let totalHeight = 0;
    for (let index = 0; index < sampleCount; index += 1) {
        totalHeight += (rows[index] as HTMLElement).offsetHeight || FALLBACK_ROW_HEIGHT_PX;
    }
    return Math.max(1, Math.round(totalHeight / sampleCount));
};

const trackTableScroll = (tableBody: HTMLElement, totalRows: number) => {
    if (totalRows <= 0) return;

    const { scrollTop, clientHeight, scrollHeight } = tableBody;
    const rowHeight = getAverageRowHeightPx(tableBody);
    const visibleRowStart = Math.min(totalRows, Math.floor(scrollTop / rowHeight) + 1);
    const visibleRowEnd = Math.min(
        totalRows,
        Math.max(visibleRowStart, Math.ceil((scrollTop + clientHeight) / rowHeight)),
    );
    const scrollDepthPct =
        scrollHeight > clientHeight
            ? Math.round((scrollTop / (scrollHeight - clientHeight)) * 100)
            : 100;

    trackEventMixpanel(PPA_PACK_ARCH_TABLE_SCROLLED, {
        visible_row_start: visibleRowStart,
        visible_row_end: visibleRowEnd,
        total_rows: totalRows,
        scroll_depth_pct: scrollDepthPct,
    });
};

type TableScrollTrackState = {
    lastScrollTop: number;
    lastScrollLeft: number;
    debounceTimer: ReturnType<typeof setTimeout> | null;
};

/** Updates last scroll position; schedules Mixpanel when the scroll is user-driven. */
const maybeTrackUserTableScroll = (
    target: HTMLElement,
    totalRows: number,
    isProgrammaticScroll: boolean,
    state: TableScrollTrackState,
) => {
    const scrollTop = target.scrollTop;
    const scrollLeft = target.scrollLeft;
    const topChanged = Math.abs(scrollTop - state.lastScrollTop) >= 1;
    const leftChanged = Math.abs(scrollLeft - state.lastScrollLeft) >= 1;
    if (!topChanged && !leftChanged) return;

    state.lastScrollTop = scrollTop;
    state.lastScrollLeft = scrollLeft;
    if (isProgrammaticScroll) return;

    if (state.debounceTimer) clearTimeout(state.debounceTimer);
    state.debounceTimer = setTimeout(() => {
        trackTableScroll(target, totalRows);
    }, TABLE_SCROLL_TRACK_DEBOUNCE_MS);
};

const restoreScrollAfterPagination = (
    tableWrapper: HTMLElement,
    snapshot: ScrollSnapshot,
    isRestoringScrollRef: MutableRefObject<boolean>,
) => {
    const tableBody = tableWrapper.querySelector('.ant-table-body') as HTMLElement | null;
    if (!tableBody) return;

    const widthGrowth = tableBody.scrollWidth - snapshot.scrollWidth;
    const targetScrollLeft =
        widthGrowth > 0
            ? snapshot.scrollLeft + widthGrowth - TABLE_PAGINATION_SCROLL_RETREAT_PX
            : snapshot.scrollLeft - TABLE_PAGINATION_SCROLL_RETREAT_PX;

    isRestoringScrollRef.current = true;
    setPackArchitectureTableScrollLeft(tableWrapper, Math.max(0, targetScrollLeft));
    requestAnimationFrame(() => {
        isRestoringScrollRef.current = false;
    });
};

function usePackArchitectureTableSelectionScroll({
    tableWrapperRef,
    activeSegmentKey,
    selectedColumnKey,
    columnIndexByKey,
    isTablePaginatedLoading,
    builtTableRowCount,
    rowIndexByKey,
    rowHeightPx,
}: {
    tableWrapperRef: RefObject<HTMLDivElement | null>;
    activeSegmentKey: string | null;
    selectedColumnKey: string | null;
    columnIndexByKey?: Map<string, number>;
    isTablePaginatedLoading: boolean;
    builtTableRowCount: number;
    rowIndexByKey?: Map<string, number>;
    rowHeightPx?: number;
}) {
    useEffect(() => {
        if (!activeSegmentKey || isTablePaginatedLoading) return;

        const tableBody = tableWrapperRef.current?.querySelector('.ant-table-body');
        if (!tableBody) return;

        const selectedRow = tableBody.querySelector(`[data-row-key="${CSS.escape(activeSegmentKey)}"]`);
        if (selectedRow) {
            selectedRow.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } else if (typeof rowHeightPx === 'number' && rowHeightPx > 0 && rowIndexByKey?.has(activeSegmentKey)) {
            const selectedRowIndex = rowIndexByKey.get(activeSegmentKey) as number;
            const targetTop = Math.max(0, selectedRowIndex * rowHeightPx - tableBody.clientHeight / 2 + rowHeightPx / 2);
            tableBody.scrollTo({ top: targetTop, behavior: 'smooth' });
        }

        if (selectedColumnKey) {
            const selectedCell =
                tableBody.querySelector(
                    `[data-row-key="${CSS.escape(activeSegmentKey)}"].vendor-cell-selected`,
                ) ??
                tableBody.querySelector(
                    `[data-row-key="${CSS.escape(activeSegmentKey)}"] .vendor-cell-selected`,
                );
            if (selectedCell) {
                selectedCell.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
            } else if (columnIndexByKey?.has(selectedColumnKey)) {
                const selectedColumnIndex = columnIndexByKey.get(selectedColumnKey) as number;
                const targetLeft =
                    TABLE_SEGMENT_COLUMN_WIDTH_PX +
                    selectedColumnIndex * TABLE_VENDOR_COLUMN_WIDTH_PX -
                    tableBody.clientWidth / 2 +
                    TABLE_VENDOR_COLUMN_WIDTH_PX / 2;
                tableBody.scrollTo({ left: Math.max(0, targetLeft), behavior: 'smooth' });
            }
        }
    }, [
        activeSegmentKey,
        builtTableRowCount,
        columnIndexByKey,
        isTablePaginatedLoading,
        rowHeightPx,
        rowIndexByKey,
        selectedColumnKey,
        tableWrapperRef,
    ]);
}

export function usePackArchitectureTableScroll({
    tableWrapperRef,
    builtTableRowCount,
    visibleColumnCount,
    loadedColumnCount,
    tableHasMorePages,
    isChartLoading,
    isTablePaginatedLoading,
    isChartPaginatedLoading,
    fetchNextTablePage,
    activeSegmentKey,
    selectedColumnKey,
    columnIndexByKey,
    rowIndexByKey,
    rowHeightPx,
    isSyncingTableFromChartRef,
    suppressChartToTableSyncRef,
}: UsePackArchitectureTableScrollParams) {
    const isFetchingNextPageRef = useRef(false);
    const isRestoringScrollRef = useRef(false);
    const scrollSnapshotRef = useRef<ScrollSnapshot | null>(null);
    const suppressChartSyncClearTimeoutRef = useRef<number | null>(null);
    const tableScrollRafRef = useRef<number | null>(null);
    const pendingTableScrollTargetRef = useRef<HTMLElement | null>(null);
    const tableScrollTrackStateRef = useRef<TableScrollTrackState>({
        lastScrollTop: 0,
        lastScrollLeft: 0,
        debounceTimer: null,
    });
    const scheduleSuppressSyncClear = useCallback(() => {
        if (!suppressChartToTableSyncRef) return;
        if (suppressChartSyncClearTimeoutRef.current !== null) {
            window.clearTimeout(suppressChartSyncClearTimeoutRef.current);
        }
        suppressChartSyncClearTimeoutRef.current = window.setTimeout(() => {
            suppressChartToTableSyncRef.current = false;
            suppressChartSyncClearTimeoutRef.current = null;
        }, TABLE_PAGINATION_SUPPRESS_CHART_SYNC_CLEAR_MS);
    }, [suppressChartToTableSyncRef]);

    const checkAndFetchTablePage = useCallback(
        async (target: HTMLElement) => {
            if (isRestoringScrollRef.current || isSyncingTableFromChartRef?.current) return;

            const reachedRightEdge =
                target.scrollLeft + target.clientWidth >=
                target.scrollWidth - HORIZONTAL_PAGINATION_RIGHT_EDGE_THRESHOLD_PX;

            if (
                !reachedRightEdge ||
                !tableHasMorePages ||
                isChartLoading ||
                isChartPaginatedLoading ||
                isTablePaginatedLoading ||
                isFetchingNextPageRef.current
            ) {
                return;
            }

            scrollSnapshotRef.current = {
                scrollLeft: target.scrollLeft,
                scrollWidth: target.scrollWidth,
                columnCountBeforeFetch: loadedColumnCount,
            };
            if (suppressChartToTableSyncRef) {
                suppressChartToTableSyncRef.current = true;
            }
            isFetchingNextPageRef.current = true;

            try {
                await fetchNextTablePage();
            } finally {
                isFetchingNextPageRef.current = false;
                // Safety release: if pagination does not append columns (or fails),
                // do not leave chart->table sync suppressed forever.
                if (suppressChartToTableSyncRef?.current) {
                    scheduleSuppressSyncClear();
                }
            }
        },
        [
            fetchNextTablePage,
            isChartLoading,
            isChartPaginatedLoading,
            isTablePaginatedLoading,
            isSyncingTableFromChartRef,
            loadedColumnCount,
            scheduleSuppressSyncClear,
            suppressChartToTableSyncRef,
            tableHasMorePages,
        ],
    );

    const handleTableScroll = useCallback(
        (event: Event) => {
            const target = event.target;
            if (!(target instanceof HTMLElement)) return;
            // Ant Design may nest scrollables; only track the table body scroller.
            if (!target.classList.contains('ant-table-body')) return;

            pendingTableScrollTargetRef.current = target;
            if (tableScrollRafRef.current !== null) return;
            tableScrollRafRef.current = requestAnimationFrame(() => {
                tableScrollRafRef.current = null;
                const latestTarget = pendingTableScrollTargetRef.current;
                if (!latestTarget) return;

                maybeTrackUserTableScroll(
                    latestTarget,
                    builtTableRowCount,
                    isRestoringScrollRef.current || !!isSyncingTableFromChartRef?.current,
                    tableScrollTrackStateRef.current,
                );

                void checkAndFetchTablePage(latestTarget);
            });
        },
        [builtTableRowCount, checkAndFetchTablePage, isSyncingTableFromChartRef],
    );

    // Capture on the wrapper so we still receive scrolls after Ant Table mounts
    // `.ant-table-body` asynchronously (direct body binding can miss that mount).
    useEffect(() => {
        const wrapper = tableWrapperRef.current;
        if (!wrapper) return undefined;

        wrapper.addEventListener('scroll', handleTableScroll, { capture: true, passive: true });
        return () => wrapper.removeEventListener('scroll', handleTableScroll, true);
    }, [handleTableScroll, builtTableRowCount, visibleColumnCount, tableWrapperRef]);

    useEffect(() => {
        const trackState = tableScrollTrackStateRef.current;
        return () => {
            if (tableScrollRafRef.current !== null) {
                cancelAnimationFrame(tableScrollRafRef.current);
                tableScrollRafRef.current = null;
            }
            if (trackState.debounceTimer) {
                clearTimeout(trackState.debounceTimer);
                trackState.debounceTimer = null;
            }
            if (suppressChartSyncClearTimeoutRef.current !== null) {
                window.clearTimeout(suppressChartSyncClearTimeoutRef.current);
                suppressChartSyncClearTimeoutRef.current = null;
            }
            if (suppressChartToTableSyncRef) {
                suppressChartToTableSyncRef.current = false;
            }
        };
    }, [suppressChartToTableSyncRef]);

    useLayoutEffect(() => {
        const snapshot = scrollSnapshotRef.current;
        const tableWrapper = tableWrapperRef.current;

        if (
            !snapshot ||
            !tableWrapper ||
            isTablePaginatedLoading ||
            loadedColumnCount <= snapshot.columnCountBeforeFetch
        ) {
            return;
        }

        restoreScrollAfterPagination(tableWrapper, snapshot, isRestoringScrollRef);
    }, [isTablePaginatedLoading, loadedColumnCount, tableWrapperRef]);

    useEffect(() => {
        const snapshot = scrollSnapshotRef.current;
        const tableWrapper = tableWrapperRef.current;

        if (
            !snapshot ||
            !tableWrapper ||
            isTablePaginatedLoading ||
            loadedColumnCount <= snapshot.columnCountBeforeFetch
        ) {
            return;
        }

        const timeoutIds = PAGINATION_SCROLL_RETRY_MS.map((delayMs) =>
            window.setTimeout(() => {
                restoreScrollAfterPagination(tableWrapper, snapshot, isRestoringScrollRef);
            }, delayMs),
        );

        if (suppressChartToTableSyncRef) {
            scheduleSuppressSyncClear();
        }

        scrollSnapshotRef.current = null;

        return () => {
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [
        isTablePaginatedLoading,
        loadedColumnCount,
        scheduleSuppressSyncClear,
        suppressChartToTableSyncRef,
        tableWrapperRef,
        visibleColumnCount,
    ]);

    usePackArchitectureTableSelectionScroll({
        tableWrapperRef,
        activeSegmentKey,
        selectedColumnKey,
        columnIndexByKey,
        isTablePaginatedLoading,
        builtTableRowCount,
        rowIndexByKey,
        rowHeightPx,
    });
}
